// The restaurant's customer book, seen from a store: search by name or
// phone fragment, get-or-create by phone, and a slide-in profile with the
// opt-ins, notes, addresses (client-chosen ids), this store's order
// history and the loyalty cards. Customers belong to the restaurant, never
// a branch — only the order history is per store.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Plus, Trash2, X } from 'lucide-react';
import { api, money, dateTimeOf, label, type Order } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import {
  messageOf,
  type AddressInput,
  type Customer,
  type CustomerAddress,
  type CustomerUpdate,
  type GetOrCreateCustomerInput,
  type LoyaltyCard,
} from './types';

type ProfileForm = { name: string; phone: string; email: string; notes: string; sms: boolean; emailOptIn: boolean };
type AddressForm = {
  addressId: string;
  isNew: boolean;
  label: string;
  addressLine: string;
  suburb: string;
  state: string;
  postcode: string;
  isDefault: boolean;
};
type NewCustomer = { phone: string; name: string; email: string };

/** The client chooses an address id; the PUT is an upsert on it. */
const newAddressId = () => 'addr_' + Math.random().toString(36).slice(2, 10);

const profileOf = (c: Customer): ProfileForm => ({
  name: c.name,
  phone: c.phoneE164 ?? '',
  email: c.email ?? '',
  notes: c.notes ?? '',
  sms: c.marketingSmsOptIn,
  emailOptIn: c.marketingEmailOptIn,
});

const optIns = (c: Customer) =>
  [c.marketingSmsOptIn ? 'SMS' : null, c.marketingEmailOptIn ? 'Email' : null].filter(Boolean).join(' · ') || '—';

export function Directory() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const R = `/restaurants/${rid}`;

  const [q, setQ] = useState('');
  const [results, setResults] = useState<Customer[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const [creating, setCreating] = useState<NewCustomer | null>(null);

  // the slide-in profile
  const [sel, setSel] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [cards, setCards] = useState<LoyaltyCard[] | null>(null);
  const [profile, setProfile] = useState<ProfileForm | null>(null);
  const [addrForm, setAddrForm] = useState<AddressForm | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const search = useCallback(
    async (term: string) => {
      const mine = ++seq.current;
      if (!term.trim()) {
        setResults(null);
        return;
      }
      setSearching(true);
      try {
        const rs = await api.get<Customer[]>(`${R}/customers`, { q: term.trim() });
        if (mine !== seq.current) return; // a newer term is in flight
        setResults(rs);
        setError(null);
      } catch (e) {
        if (mine === seq.current) setError(messageOf(e, 'Search failed.'));
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    },
    [R],
  );

  useEffect(() => {
    const t = setTimeout(() => void search(q), 250);
    return () => clearTimeout(t);
  }, [q, search]);

  /** A write returned the customer whole: show it and refresh its result row. */
  const applyCustomer = useCallback((c: Customer) => {
    setSel(c);
    setProfile(profileOf(c));
    setAddrForm(null);
    setResults((rs) => rs?.map((x) => (x.customerId === c.customerId ? c : x)) ?? rs);
  }, []);

  async function open(customerId: string) {
    setPanelError(null);
    setOrders(null);
    setCards(null);
    try {
      applyCustomer(await api.get<Customer>(`${R}/customers/${customerId}`));
      const [os, cs] = await Promise.all([
        api.get<Order[]>(`${base}/customers/${customerId}/orders`),
        api.get<LoyaltyCard[]>(`${R}/customers/${customerId}/loyalty-cards`),
      ]);
      setOrders(os);
      setCards(cs);
    } catch (e) {
      setPanelError(messageOf(e, 'Failed to load the customer.'));
    }
  }

  function close() {
    setSel(null);
    setProfile(null);
    setAddrForm(null);
    setOrders(null);
    setCards(null);
    setPanelError(null);
  }

  async function createCustomer() {
    if (!creating || !creating.phone.trim()) return;
    setBusy(true);
    try {
      const body: GetOrCreateCustomerInput = {
        phone: creating.phone.trim(),
        name: creating.name.trim() || null,
        email: creating.email.trim() || null,
      };
      const c = await api.post<Customer>(`${R}/customers`, body);
      setCreating(null);
      setError(null);
      setQ(c.phoneE164 ?? body.phone);
      await open(c.customerId);
    } catch (e) {
      setError(messageOf(e, 'Could not create the customer.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    if (!sel || !profile) return;
    setBusy(true);
    try {
      const body: CustomerUpdate = {
        name: profile.name.trim(),
        phone: profile.phone.trim() || null,
        email: profile.email.trim() || null,
        notes: profile.notes.trim() || null,
        marketingSmsOptIn: profile.sms,
        marketingEmailOptIn: profile.emailOptIn,
      };
      applyCustomer(await api.put<Customer>(`${R}/customers/${sel.customerId}`, body));
      setPanelError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setPanelError(messageOf(e, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveAddress() {
    if (!sel || !addrForm) return;
    setBusy(true);
    try {
      const body: AddressInput = {
        label: addrForm.label.trim() || null,
        addressLine: addrForm.addressLine.trim(),
        suburb: addrForm.suburb.trim(),
        state: addrForm.state.trim(),
        postcode: addrForm.postcode.trim(),
        lat: null,
        lng: null,
        notes: null,
        isDefault: addrForm.isDefault,
      };
      applyCustomer(await api.put<Customer>(`${R}/customers/${sel.customerId}/addresses/${addrForm.addressId}`, body));
      setPanelError(null);
    } catch (e) {
      setPanelError(messageOf(e, 'Address save failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function deleteAddress(a: CustomerAddress) {
    if (!sel || !confirm(`Delete the address “${a.label ?? a.addressLine}”?`)) return;
    setBusy(true);
    try {
      applyCustomer(await api.del<Customer>(`${R}/customers/${sel.customerId}/addresses/${a.addressId}`));
      setPanelError(null);
    } catch (e) {
      setPanelError(messageOf(e, 'Delete failed.'));
    } finally {
      setBusy(false);
    }
  }

  const editAddress = (a: CustomerAddress) =>
    setAddrForm({
      addressId: a.addressId,
      isNew: false,
      label: a.label ?? '',
      addressLine: a.addressLine,
      suburb: a.suburb,
      state: a.state,
      postcode: a.postcode,
      isDefault: a.isDefault,
    });

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Customers · Directory</div>
          <h1>Customers</h1>
        </div>
        <button
          className="btn btn-primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setCreating(creating ? null : { phone: '', name: '', email: '' })}
        >
          <Plus size={16} /> New customer
        </button>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {creating && (
        <div className="panel" data-section="new-customer" style={{ marginBottom: 20 }}>
          <h2>New customer</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', paddingTop: 8 }}>
            <div className="field">
              <label>Phone</label>
              <input className="input" aria-label="New customer phone" placeholder="04…" value={creating.phone}
                onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label>Name</label>
              <input className="input" aria-label="New customer name" placeholder="Full name" value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label>Email</label>
              <input className="input" aria-label="New customer email" type="email" placeholder="Optional" value={creating.email}
                onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
            </div>
            <button className="btn btn-primary" disabled={busy || !creating.phone.trim()} onClick={() => void createCustomer()}>
              {busy ? 'Saving…' : 'Find or create'}
            </button>
          </div>
          <div style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 8 }}>
            Phone is the identity — an existing customer with this phone is opened instead of duplicated.
          </div>
        </div>
      )}

      <div className="filters" style={{ marginBottom: 18 }}>
        <input
          className="input"
          aria-label="Search customers"
          placeholder="Search by name or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 380 }}
        />
        {searching && <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>Searching…</span>}
      </div>

      {!q.trim() && <div className="loading">Type to search the directory — a name or any part of a phone number.</div>}
      {q.trim() && results?.length === 0 && !searching && <div className="loading">No customers match “{q.trim()}”.</div>}
      {results && results.length > 0 && (
        <table className="tbl" data-section="results">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Marketing opt-ins</th>
            </tr>
          </thead>
          <tbody>
            {results.map((c) => (
              <tr key={c.customerId} data-customer-id={c.customerId} onClick={() => void open(c.customerId)}>
                <td style={{ fontWeight: 700 }}>{c.name || '—'}</td>
                <td className="money">{c.phoneE164 ?? '—'}</td>
                <td style={{ color: 'var(--ink-2)' }}>{c.email ?? '—'}</td>
                <td style={{ color: 'var(--ink-2)' }}>{optIns(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sel && profile && (
        <div
          data-section="customer"
          data-customer-id={sel.customerId}
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 94vw)',
            background: 'var(--bg)', borderLeft: '1px solid var(--line-strong)',
            boxShadow: '-24px 0 48px color-mix(in srgb, var(--ink) 12%, transparent)',
            padding: '22px 26px', overflowY: 'auto', zIndex: 60,
            display: 'flex', flexDirection: 'column', gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="overline">Customer</div>
              <h2 style={{ margin: 0, fontSize: 24 }} data-field="name">{sel.name || sel.phoneE164 || sel.customerId}</h2>
              <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }} data-field="contact">
                {sel.phoneE164 ?? 'No phone'}{sel.email ? ` · ${sel.email}` : ''}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={close} aria-label="Close customer">
              <X size={18} />
            </button>
          </div>
          {panelError && <div className="err" role="alert" style={{ margin: 0 }}>{panelError}</div>}

          <div className="panel" data-section="profile">
            <h2>Profile {saved && <span style={{ color: 'var(--ok)', textTransform: 'none', letterSpacing: 0 }}>· saved ✓</span>}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingTop: 8 }}>
              <div className="field">
                <label>Name</label>
                <input className="input" aria-label="Customer name" value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
              </div>
              <div className="field">
                <label>Phone</label>
                <input className="input" aria-label="Customer phone" value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Email</label>
                <input className="input" aria-label="Customer email" type="email" value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <label>Notes</label>
              <textarea className="input" aria-label="Customer notes" rows={2} placeholder="Prefers window seats…" value={profile.notes}
                onChange={(e) => setProfile({ ...profile, notes: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={profile.sms}
                  onChange={(e) => setProfile({ ...profile, sms: e.target.checked })} />
                SMS marketing
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={profile.emailOptIn}
                  onChange={(e) => setProfile({ ...profile, emailOptIn: e.target.checked })} />
                Email marketing
              </label>
            </div>
            <button className="btn btn-primary" style={{ marginTop: 14 }}
              disabled={busy || !profile.name.trim()} onClick={() => void saveProfile()}>
              {busy ? 'Saving…' : 'Save profile'}
            </button>
          </div>

          <div className="panel" data-section="addresses">
            <h2>Addresses</h2>
            {sel.addresses.length === 0 && !addrForm && <div className="loading">No addresses on file.</div>}
            {sel.addresses.map((a) => (
              <div key={a.addressId} className="rowline" data-address-id={a.addressId}>
                <div className="grow">
                  <b>
                    {a.label ?? 'Address'}
                    {a.isDefault && <span data-default style={{ color: 'var(--primary-ink)', fontSize: 11.5, marginLeft: 8 }}>DEFAULT</span>}
                  </b>
                  <span className="sub">{a.addressLine}, {a.suburb} {a.state} {a.postcode}</span>
                </div>
                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => editAddress(a)}
                  aria-label={`Edit address ${a.label ?? a.addressLine}`}>Edit</button>
                <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => void deleteAddress(a)}
                  aria-label={`Delete address ${a.label ?? a.addressLine}`} title="Delete address">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {addrForm ? (
              <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>Label</label>
                    <input className="input" aria-label="Address label" placeholder="Home, Work…" value={addrForm.label}
                      onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Street</label>
                    <input className="input" aria-label="Street" value={addrForm.addressLine}
                      onChange={(e) => setAddrForm({ ...addrForm, addressLine: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Suburb</label>
                    <input className="input" aria-label="Suburb" value={addrForm.suburb}
                      onChange={(e) => setAddrForm({ ...addrForm, suburb: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="field">
                      <label>State</label>
                      <input className="input" aria-label="State" value={addrForm.state}
                        onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} />
                    </div>
                    <div className="field">
                      <label>Postcode</label>
                      <input className="input" aria-label="Postcode" value={addrForm.postcode}
                        onChange={(e) => setAddrForm({ ...addrForm, postcode: e.target.value })} />
                    </div>
                  </div>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginTop: 10 }}>
                  <input type="checkbox" checked={addrForm.isDefault}
                    onChange={(e) => setAddrForm({ ...addrForm, isDefault: e.target.checked })} />
                  Default delivery address
                </label>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void saveAddress()}>
                    {busy ? 'Saving…' : addrForm.isNew ? 'Save new address' : 'Save address'}
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setAddrForm(null)}>
                    Discard
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginTop: 8 }}
                onClick={() =>
                  setAddrForm({
                    addressId: newAddressId(), isNew: true, label: '', addressLine: '',
                    suburb: '', state: '', postcode: '', isDefault: sel.addresses.length === 0,
                  })
                }
              >
                <Plus size={14} /> Add address
              </button>
            )}
          </div>

          <div className="panel" data-section="order-history">
            <h2>Order history · this store</h2>
            {orders === null && <div className="loading">Loading…</div>}
            {orders?.length === 0 && <div className="loading">No orders at this store.</div>}
            {orders?.map((o) => (
              <Link
                key={o.orderId}
                className="rowline"
                data-order-id={o.orderId}
                style={{ textDecoration: 'none', color: 'inherit' }}
                to={`/r/${rid}/s/${sid}/orders/${o.orderId}`}
              >
                <span className="docket">{o.docketLabel ? `#${o.docketLabel}` : '—'}</span>
                <div className="grow">
                  <b>{label(o.collectionType)} · {money(o.grandTotalInCents)}</b>
                  <span className="sub">
                    {dateTimeOf(o.placedAt, store?.timezone)}
                    {o.couponCode ? ` · coupon ${o.couponCode}` : ''}
                  </span>
                </div>
                <span className={`chip chip-${o.status}`}>{label(o.status)}</span>
              </Link>
            ))}
          </div>

          <div className="panel" data-section="loyalty-cards">
            <h2>Loyalty cards</h2>
            {cards === null && <div className="loading">Loading…</div>}
            {cards?.length === 0 && <div className="loading">No loyalty cards.</div>}
            {cards?.map((c) => {
              const state = !c.isOpen ? 'closed' : c.isRedeemable ? 'redeemable' : 'accruing';
              return (
                <div key={c.cardId} className="rowline" data-card-state={state}>
                  <div className="grow">
                    <b>
                      {money(c.valueInCents)} banked · {c.rewards.length}
                      {c.ordersPerCard > 0 ? ` of ${c.ordersPerCard}` : ''} orders
                    </b>
                    <span className="sub">
                      Opened {dateTimeOf(c.createdAt, store?.timezone)}
                      {c.redeemedAt ? ` · redeemed ${dateTimeOf(c.redeemedAt, store?.timezone)}` : ''}
                    </span>
                  </div>
                  <span className={`chip chip-${state === 'closed' ? 'cancelled' : state === 'redeemable' ? 'completed' : 'pending'}`}>
                    {state}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
